import { BrazilTimeZone } from '@/utils/constants';
import { addMinutes, format } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { WeekDaysEnum } from './types';

export const formatDate = (timeStamp: number): string => {
  const date = ZonedDate(timeStamp);

  return format(date, 'dd.MM.yy');
};

export const getUTCDate = (timeStamp: number): Date => {
  const utcDate = ZonedDate(timeStamp);
  const utcYear = utcDate.getUTCFullYear();
  const utcMonth = utcDate.getUTCMonth();
  const utcDay = utcDate.getUTCDate();
  const utcHours = utcDate.getUTCHours();
  const utcMinutes = utcDate.getUTCMinutes();
  const utcSeconds = utcDate.getUTCSeconds();
  const utcMilliseconds = utcDate.getUTCMilliseconds();
  return ZonedDate(new Date(utcYear, utcMonth, utcDay, utcHours, utcMinutes, utcSeconds, utcMilliseconds));
};

export const formatSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const formatRetryTime = (seconds: number, locale: string = 'pt-BR'): string => {
  if (seconds < 60) {
    if (locale === 'pt-BR' || locale === 'es-ES') {
      return seconds === 1 ? '1 segundo' : `${seconds} segundos`;
    }
    return seconds === 1 ? '1 second' : `${seconds} seconds`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainingSeconds === 0) {
      if (locale === 'pt-BR' || locale === 'es-ES') {
        return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
      }
      return minutes === 1 ? '1 minute' : `${minutes} minutes`;
    }

    if (locale === 'pt-BR') {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''} e ${remainingSeconds} segundo${remainingSeconds !== 1 ? 's' : ''}`;
    } else if (locale === 'es-ES') {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''} y ${remainingSeconds} segundo${remainingSeconds !== 1 ? 's' : ''}`;
    }
    return `${minutes} minute${minutes !== 1 ? 's' : ''} and ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    if (locale === 'pt-BR' || locale === 'es-ES') {
      return hours === 1 ? '1 hora' : `${hours} horas`;
    }
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  if (locale === 'pt-BR') {
    return `${hours} hora${hours !== 1 ? 's' : ''} e ${remainingMinutes} minuto${remainingMinutes !== 1 ? 's' : ''}`;
  } else if (locale === 'es-ES') {
    return `${hours} hora${hours !== 1 ? 's' : ''} y ${remainingMinutes} minuto${remainingMinutes !== 1 ? 's' : ''}`;
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} and ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
};

export const convertTimeToDate = (time: string) => {
  const now = ZonedDate();
  const [hours, minutes] = time.split(':').map(Number);

  return ZonedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes));
};

export const convertMinutesToTime = (minutes: number) => {
  const now = ZonedDate();

  const midnight = ZonedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0));
  const time = addMinutes(midnight, minutes);

  return format(time, 'HH:mm');
};

export const getWeekDayName = (weekday: WeekDaysEnum) => {
  switch (weekday) {
    case WeekDaysEnum.SUN:
      return 'sunday';
    case WeekDaysEnum.MON:
      return 'monday';
    case WeekDaysEnum.TUE:
      return 'tuesday';
    case WeekDaysEnum.WED:
      return 'wednesday';
    case WeekDaysEnum.THU:
      return 'thursday';
    case WeekDaysEnum.FRI:
      return 'friday';
    case WeekDaysEnum.SAT:
      return 'saturday';
  }
};

export const ZonedDate = (date?: string | number | Date): Date => {
  return toZonedTime(date ? new Date(date) : new Date(), BrazilTimeZone);
};

/** `YYYY-MM-DD` em wall-clock São Paulo. Útil para comparar dias sem ambiguidade de TZ. */
export const toBrazilDateKey = (date: Date | string | number): string =>
  format(toZonedTime(new Date(date), BrazilTimeZone), 'yyyy-MM-dd');

/** Instante UTC do início do dia (00:00 SP). Útil em filtros `gte` no back. */
export const startOfSPDay = (date: Date | string | number): Date => {
  const key = toBrazilDateKey(date);
  return fromZonedTime(`${key}T00:00:00`, BrazilTimeZone);
};

/** Instante UTC do fim do dia (23:59:59.999 SP). Útil em filtros `lte` no back. */
export const endOfSPDay = (date: Date | string | number): Date => {
  const key = toBrazilDateKey(date);
  return fromZonedTime(`${key}T23:59:59.999`, BrazilTimeZone);
};
